import { Link, useLocation } from 'react-router-dom';
import { User } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import ConfigurationLayout from 'components/configuration/ConfigurationLayout';
import ConfigurationHeader from 'components/configuration/ConfigurationHeader';
import { AccountProfileTab } from './components/account-profile-tab';
import { AccountSecurityTab } from './components/account-security-tab';
import { AccountPersonalizationTab } from './components/account-personalization-tab';
import { AccountToolPermissionsTab } from './components/account-tool-permissions-tab';
import { AccountSubscriptionTab } from './components/account-subscription-tab';

export const ACCOUNT_SECURITY_PATH = '/account/security';
export const ACCOUNT_PERSONALIZATION_PATH = '/account/personalization';
export const ACCOUNT_TOOL_PERMISSIONS_PATH = '/account/tool-permissions';
export const ACCOUNT_SUBSCRIPTION_PATH = '/account/subscription';

// Figma "Account" (10596:336). Tabs without a path are not built yet.
type AccountTab = { id: string; labelKey: string; defaultLabel: string; path?: string };

const TABS: AccountTab[] = [
  { id: 'profile', labelKey: 'ACCOUNT_TAB_PROFILE', defaultLabel: 'Profile', path: '/account' },
  {
    id: 'security',
    labelKey: 'ACCOUNT_TAB_SECURITY',
    defaultLabel: 'Security',
    path: ACCOUNT_SECURITY_PATH,
  },
  {
    id: 'personalization',
    labelKey: 'ACCOUNT_TAB_PERSONALIZATION',
    defaultLabel: 'Personalization',
    path: ACCOUNT_PERSONALIZATION_PATH,
  },
  {
    id: 'tool-permissions',
    labelKey: 'ACCOUNT_TAB_TOOL_PERMISSIONS',
    defaultLabel: 'Tool permissions',
    path: ACCOUNT_TOOL_PERMISSIONS_PATH,
  },
  {
    id: 'subscription',
    labelKey: 'ACCOUNT_TAB_SUBSCRIPTION',
    defaultLabel: 'Subscription',
    path: ACCOUNT_SUBSCRIPTION_PATH,
  },
  // Hidden until the Models tab is built.
  // { id: 'models', labelKey: 'ACCOUNT_TAB_MODELS', defaultLabel: 'Models' },
];

export function AccountPage() {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  let tabContent = <AccountProfileTab />;
  if (pathname === ACCOUNT_SECURITY_PATH) tabContent = <AccountSecurityTab />;
  if (pathname === ACCOUNT_PERSONALIZATION_PATH) tabContent = <AccountPersonalizationTab />;
  if (pathname === ACCOUNT_TOOL_PERMISSIONS_PATH) tabContent = <AccountToolPermissionsTab />;
  if (pathname === ACCOUNT_SUBSCRIPTION_PATH) tabContent = <AccountSubscriptionTab />;

  return (
    <ConfigurationLayout className="gap-6 pb-8">
      <ConfigurationHeader
        title={t('ACCOUNT', { defaultValue: 'Account' })}
        subtitle={t('ACCOUNT_PAGE_SUBTITLE', {
          defaultValue: 'Your profile, how you sign in, and how Buddy works with you.',
        })}
        icon={
          <User
            width={20}
            height={20}
            strokeWidth={1.5}
            className="shrink-0 text-grey-900 dark:text-foreground"
          />
        }
      />

      <div className="flex flex-col gap-6">
        <div
          role="tablist"
          className="flex gap-7 overflow-x-auto overflow-y-hidden border-b border-grey-100 dark:border-border"
        >
          {TABS.map((tab) => {
            const isActive = tab.path === pathname;
            const className = cn(
              'flex h-11 shrink-0 items-center whitespace-nowrap border-b-2 text-sm',
              isActive
                ? 'border-grey-950 font-medium text-grey-950 dark:border-foreground dark:text-foreground'
                : 'border-transparent text-grey-600 hover:text-grey-950 dark:text-muted-foreground dark:hover:text-foreground'
            );
            const label = t(tab.labelKey, { defaultValue: tab.defaultLabel });

            if (!tab.path) {
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={false}
                  className={className}
                >
                  {label}
                </button>
              );
            }

            return (
              <Link
                key={tab.id}
                to={tab.path}
                role="tab"
                aria-selected={isActive}
                className={className}
              >
                {label}
              </Link>
            );
          })}
        </div>

        {tabContent}
      </div>
    </ConfigurationLayout>
  );
}
